import React, { useState, useCallback, useEffect, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../../AppContext';
import { sendPlayerState, subscribeToPlayerState } from '../../services/gameService';

// --- Constants ---
const BASE_GAME_WIDTH = 500;
const BASE_GAME_HEIGHT = 800;
const PLAYER_SIZE = 30;
const GRAVITY = 0.15;
const JUMP_STRENGTH = -6.0;
const PLAYER_INITIAL_Y = BASE_GAME_HEIGHT / 2 - PLAYER_SIZE / 2;
const OBSTACLE_WIDTH_MIN = 70;
const OBSTACLE_WIDTH_MAX = 110;
const OBSTACLE_GAP_MIN = 220; // Adjusted for more forgiving gameplay
const OBSTACLE_GAP_MAX = 280; // Adjusted for more forgiving gameplay
const OBSTACLE_SPEED_INITIAL = -1.5; // Slightly slower start
const OBSTACLE_SPEED_MAX = -4;
const OBSTACLE_SPEED_INCREMENT_PER_POINT = 0.02;
const OBSTACLE_INTERVAL_INITIAL = 2800;
const OBSTACLE_INTERVAL_FINAL = 1200;
const OBSTACLE_INTERVAL_DECREMENT_PER_POINT = 40;

const GameState = { Idle: 0, Playing: 1, GameOver: 2 };
const SubmissionStatus = { Idle: 0, Submitting: 1, Submitted: 2, Error: 3 };

// --- Custom Hooks ---
const useGameLoop = (callback, isRunning) => {
    const savedCallback = useRef();
    useEffect(() => { savedCallback.current = callback; }, [callback]);
    useEffect(() => {
        if (!isRunning) return;
        let lastTime = performance.now();
        let animationFrameId;
        const loop = (currentTime) => {
            const deltaTime = currentTime - lastTime;
            if (savedCallback.current) savedCallback.current(deltaTime);
            lastTime = currentTime;
            animationFrameId = requestAnimationFrame(loop);
        };
        animationFrameId = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(animationFrameId);
    }, [isRunning]);
};

const useForceUpdate = () => {
    const [, setValue] = useState(0);
    return useCallback(() => setValue(value => value + 1), []);
};

const CosmicAscent = ({ dreamWeaveId, nexusId, souls = 0, onGameSubmit, onGameOver, onClose, isSpectatorMode = false, isRemoteMode = false, syncId, myRole, remoteRole, autoStart = false }) => {
  const { t } = useTranslation();
  const { appFunctions } = useAppContext();
  const gameContainerRef = useRef(null);

  const [gameState, setGameState] = useState(GameState.Idle);
  const [score, setScore] = useState(0);
  const [dimensions, setDimensions] = useState({ width: BASE_GAME_WIDTH, height: BASE_GAME_HEIGHT, scale: 1 });
  const [jumpEffect, setJumpEffect] = useState(false);
  const [gameLog, setGameLog] = useState([]);

  const playerRef = useRef({ y: PLAYER_INITIAL_Y, velocity: 0 });
  const obstaclesRef = useRef([]);
  const trailRef = useRef([]);
  const gameStateRef = useRef(GameState.Idle);
  const obstacleTimerRef = useRef(null);
  const nextId = useRef(0);
  const jumpTimeoutRef = useRef(null);

  const forceUpdate = useForceUpdate();

  useEffect(() => { gameStateRef.current = gameState; }, [gameState]);

  // --- Sync (Remote Mode) ---
  useEffect(() => {
    if (isRemoteMode && syncId && remoteRole) {
        const unsubscribe = subscribeToPlayerState(syncId, remoteRole, (data) => {
            if (data && data.y !== undefined) {
                playerRef.current = { y: data.y, velocity: data.velocity || 0 };
                // If we receive data and game is idle, start it visually
                if (gameStateRef.current === GameState.Idle) {
                    setGameState(GameState.Playing);
                }
                forceUpdate();
            }
        });
        return () => unsubscribe();
    }
  }, [isRemoteMode, syncId, remoteRole, forceUpdate]);

  // --- Responsive Scaling ---
  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
        if (!entries || entries.length === 0) return;
        const { width } = entries[0].contentRect;
        const scale = width / BASE_GAME_WIDTH;
        setDimensions({
            width: width,
            height: BASE_GAME_HEIGHT * scale,
            scale: scale
        });
    });
    if (gameContainerRef.current) {
        resizeObserver.observe(gameContainerRef.current);
    }
    return () => resizeObserver.disconnect();
  }, []);


  const generateObstacle = useCallback(() => {
    const { scale } = dimensions;
    const newObstacle = {
      id: nextId.current++,
      x: dimensions.width / scale,
      gapY: Math.random() * (BASE_GAME_HEIGHT - OBSTACLE_GAP_MAX - 150) + 75,
      gapSize: OBSTACLE_GAP_MIN + Math.random() * (OBSTACLE_GAP_MAX - OBSTACLE_GAP_MIN),
      width: OBSTACLE_WIDTH_MIN + Math.random() * (OBSTACLE_WIDTH_MAX - OBSTACLE_WIDTH_MIN),
      scored: false,
      speed: -Math.min(Math.abs(OBSTACLE_SPEED_INITIAL) + score * OBSTACLE_SPEED_INCREMENT_PER_POINT, Math.abs(OBSTACLE_SPEED_MAX))
    };
    obstaclesRef.current.push(newObstacle);
    
    if (gameStateRef.current === GameState.Playing) {
      const currentInterval = Math.max(OBSTACLE_INTERVAL_FINAL, OBSTACLE_INTERVAL_INITIAL - score * OBSTACLE_INTERVAL_DECREMENT_PER_POINT);
      obstacleTimerRef.current = setTimeout(generateObstacle, currentInterval);
    }
  }, [score, dimensions]);

  const resetGame = useCallback(() => {
    playerRef.current = { y: PLAYER_INITIAL_Y, velocity: 0 };
    obstaclesRef.current = [];
    trailRef.current = [];
    nextId.current = 0;
    setScore(0);
    setGameState(GameState.Idle);
    setGameLog([]);
    if (obstacleTimerRef.current) clearTimeout(obstacleTimerRef.current);
    forceUpdate();
  }, [forceUpdate]);
  
  const jump = useCallback(() => {
    if (gameStateRef.current !== GameState.Playing) return;
    playerRef.current.velocity = JUMP_STRENGTH;
    setJumpEffect(true);
    if (jumpTimeoutRef.current) clearTimeout(jumpTimeoutRef.current);
    jumpTimeoutRef.current = setTimeout(() => setJumpEffect(false), 150);
  }, []);

  const startGame = useCallback(() => {
    if (gameStateRef.current === GameState.Idle) {
      setGameState(GameState.Playing);
      if (!isSpectatorMode) jump();
      obstacleTimerRef.current = setTimeout(generateObstacle, OBSTACLE_INTERVAL_INITIAL / 2);
    }
  }, [jump, generateObstacle, isSpectatorMode]);

  const handleUserInput = useCallback(() => {
    if (isSpectatorMode) return;
    if (gameStateRef.current === GameState.Idle) startGame();
    else if (gameStateRef.current === GameState.Playing) jump();
  }, [startGame, jump, isSpectatorMode]);

  useEffect(() => {
    if ((isSpectatorMode || autoStart) && gameState === GameState.Idle) {
      startGame();
    }
  }, [isSpectatorMode, gameState, startGame, autoStart]);

  // --- AI Logic ---
  useEffect(() => {
    if (!isSpectatorMode || gameState !== GameState.Playing) return;

    const playerX = BASE_GAME_WIDTH / 4;
    let lastJumpTime = 0;

    const aiLoop = () => {
      if (gameStateRef.current !== GameState.Playing) return;

      const player = playerRef.current;
      const now = performance.now();
      if (now - lastJumpTime < 200) return;

      const soulsToUse = souls || 0;
      const perfection = Math.min(soulsToUse / 25000, 1);
      const upcomingObstacle = obstaclesRef.current.find(o => o.x + o.width > playerX);

      let targetY = BASE_GAME_HEIGHT / 2;
      if (upcomingObstacle) {
        targetY = upcomingObstacle.gapY + upcomingObstacle.gapSize / 2;
      }

      let jumpDecision = false;
      const playerCenter = player.y + PLAYER_SIZE / 2;
      const tolerance = 75 * (1 - perfection);

      if (playerCenter > targetY + tolerance || (player.y > BASE_GAME_HEIGHT - 80 && player.velocity > 0)) {
          jumpDecision = true;
      }
      
      if (jumpDecision) {
        const executionErrorChance = 0.4 * (1 - perfection);
        if (Math.random() > executionErrorChance) {
          jump();
          lastJumpTime = now;
        }
      }
    };

    const intervalId = setInterval(aiLoop, 1000 / 60);
    return () => clearInterval(intervalId);
  }, [isSpectatorMode, gameState, souls, jump]);

  useEffect(() => {
    if (!isSpectatorMode) {
        const handleKeyDown = (e) => {
            if (e.code === 'Space' || e.code === 'ArrowUp') {
                e.preventDefault();
                handleUserInput();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [handleUserInput, isSpectatorMode]);

  const updateGame = useCallback((deltaTime) => {
    if (gameStateRef.current !== GameState.Playing) return;

    const dt = deltaTime / (1000 / 60);
    const scaledGravity = GRAVITY * dimensions.scale;
    const playerState = playerRef.current;
    const newVelocity = playerState.velocity + scaledGravity * dt;
    const newY = playerState.y + newVelocity * dt * dimensions.scale;
    playerRef.current = { y: newY, velocity: newVelocity };

    const playerX = BASE_GAME_WIDTH / 4;
    trailRef.current.unshift({ x: playerX, y: playerRef.current.y });
    if (trailRef.current.length > 15) trailRef.current.pop();

    obstaclesRef.current = obstaclesRef.current
      .map(o => ({ ...o, x: o.x + o.speed * dt }))
      .filter(o => o.x > -OBSTACLE_WIDTH_MAX);
    
    const player = playerRef.current;
    if (player.y < 0 || player.y + (PLAYER_SIZE) > BASE_GAME_HEIGHT) {
      setGameState(GameState.GameOver);
      return;
    }
    
    for (const obstacle of obstaclesRef.current) {
      const inHorizontalRange = (playerX + PLAYER_SIZE) > obstacle.x && playerX < (obstacle.x + obstacle.width);
      if(inHorizontalRange) {
        if (player.y < obstacle.gapY || player.y + PLAYER_SIZE > obstacle.gapY + obstacle.gapSize) {
          setGameState(GameState.GameOver);
          return;
        }
      }
      if (!obstacle.scored && obstacle.x + obstacle.width < playerX) {
        obstacle.scored = true;
        setScore(s => s + 1);
      }
    }

    if (!isSpectatorMode) {
        setGameLog(prevLog => [...prevLog, { player: { ...playerRef.current }, obstacles: [...obstaclesRef.current], action: 'none'}]);
        
        if (!isRemoteMode && syncId && myRole) {
            sendPlayerState(syncId, myRole, { y: playerRef.current.y, velocity: playerRef.current.velocity });
        }
    }
    forceUpdate();
  }, [forceUpdate, isSpectatorMode, dimensions.scale]);

  useGameLoop(updateGame, gameState === GameState.Playing);

  useEffect(() => {
    if (gameState === GameState.GameOver) {
        if(obstacleTimerRef.current) clearTimeout(obstacleTimerRef.current);
        if (isSpectatorMode) {
            setTimeout(() => {
                resetGame();
                startGame();
            }, 2000);
        } else {
            if (onGameSubmit) {
                onGameSubmit('cosmicAscent', score, gameLog);
            }
            if (onGameOver) {
                onGameOver();
            }
        }
    }
  }, [gameState, isSpectatorMode, resetGame, startGame, onGameSubmit, onGameOver, score, gameLog]);

  const { width, height, scale } = dimensions;

  return (
    <div className="w-full h-full flex items-center justify-center bg-transparent p-4" ref={gameContainerRef}>
        <div style={{ maxWidth: '100%', maxHeight: '100%', width: dimensions.width, aspectRatio: `${BASE_GAME_WIDTH} / ${BASE_GAME_HEIGHT}` }}>
            <div
                className="relative overflow-hidden cursor-pointer select-none bg-gradient-to-b from-[#16132e] to-[#0d0b1a] shadow-2xl shadow-black/50 border-2 border-fuchsia-500/30 rounded-lg"
                style={{ width: '100%', height: '100%' }}
                onClick={handleUserInput}
                onTouchStart={handleUserInput}
            >
                <div className="absolute inset-0 z-0">{[...Array(50)].map((_, i) => <div key={i} className="absolute bg-white rounded-full opacity-50 animate-pulse" style={{ width: `${(Math.random()*2+1) * scale}px`, height: `${(Math.random()*2+1) * scale}px`, top: `${Math.random()*100}%`, left: `${Math.random()*100}%`}}></div>)}</div>
                
                {/* Scaled Game Elements */}
                <div className={`absolute z-20 rounded-full bg-cyan-400 ${jumpEffect ? 'jump-effect' : ''} ${gameState === GameState.GameOver ? 'game-over-effect' : ''}`}
                     style={{ width: PLAYER_SIZE * scale, height: PLAYER_SIZE * scale, transform: `translate(${BASE_GAME_WIDTH / 4 * scale}px, ${playerRef.current.y * scale}px)`, boxShadow: '0 0 15px 5px rgba(0, 255, 255, 0.7), 0 0 25px 10px rgba(0, 255, 255, 0.4)'}} />
                
                {trailRef.current.map((p, index) => {
                    const trailSize = PLAYER_SIZE * (1 - index / trailRef.current.length) * 0.6 * scale;
                    return <div key={index} className="absolute z-10 rounded-full bg-cyan-300 pointer-events-none" style={{ width: trailSize, height: trailSize, transform: `translate(${p.x * scale + (PLAYER_SIZE * scale - trailSize) / 2}px, ${p.y * scale + (PLAYER_SIZE * scale - trailSize) / 2}px)`, opacity: 0.6 * (1 - index / trailRef.current.length), filter: `blur(${index / trailRef.current.length * 2}px)`}} />
                })}

                {obstaclesRef.current.map(o => (
                    <div key={o.id}>
                        <div style={{ transform: `translateX(${o.x * scale}px)`, top: 0, width: o.width * scale, height: o.gapY * scale, position: 'absolute', backgroundColor: 'rgba(236, 72, 153, 0.5)', borderBottom: '2px solid #ec4899' }} />
                        <div style={{ transform: `translateX(${o.x * scale}px)`, top: (o.gapY + o.gapSize) * scale, width: o.width * scale, height: (BASE_GAME_HEIGHT - (o.gapY + o.gapSize)) * scale, position: 'absolute', backgroundColor: 'rgba(236, 72, 153, 0.5)', borderTop: '2px solid #ec4899' }} />
                    </div>
                ))}

                {gameState === GameState.Playing && <div className="absolute top-10 left-1/2 -translate-x-1/2 text-white font-bold" style={{fontSize: `${6 * scale}rem`, textShadow: '0 0 10px #fff'}}>{score}</div>}
                
                {/* UI Overlay */}
                <div className="absolute inset-0 z-30 flex flex-col items-center justify-center pointer-events-none">
                    {gameState === GameState.Idle && !isSpectatorMode && (
                        <div className="text-white text-center animate-fade-in p-4">
                            <h1 className="font-extrabold mb-4 text-4xl md:text-5xl">{t('cosmicAscent.title')}</h1>
                            <p className="animate-pulse text-lg md:text-xl">{t('cosmicAscent.instructions')}</p>
                        </div>
                    )}
                    {gameState === GameState.GameOver && !isSpectatorMode && (
                        <div className="bg-black/50 p-4 md:p-8 rounded-lg backdrop-blur-sm text-white text-center animate-fade-in pointer-events-auto">
                            <h2 className="font-bold mb-2 text-3xl md:text-4xl">{t('cosmicAscent.gameOver')}</h2>
                            <p className="mb-4 text-2xl md:text-3xl">{t('cosmicAscent.score', { score })}</p>
                            
                            <div className="mt-4 mb-6 p-3 bg-red-900/30 border border-red-500/50 rounded-lg text-center inline-block w-full">
                                <p className="text-sm text-red-200 uppercase tracking-widest font-bold mb-1">Raid Boss Damage</p>
                                <p className="text-3xl font-orbitron text-red-400">+{Math.floor(score * 20)}</p>
                            </div>

                            <button onClick={onClose} className="w-full px-6 py-3 bg-fuchsia-600 hover:bg-fuchsia-500 rounded-lg font-bold text-base md:text-lg shadow-lg">{t('cosmicAscent.close')}</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    </div>
    );
};

export default CosmicAscent;